import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Avatar
} from "@mui/material";
import { MdClose, MdSend, MdAssignment, MdPerson } from "react-icons/md";
import { TASKCHAT, TASKCHATALL, TASKALL } from "../../services/actions";
import VoiceMicButton from "../../components/VoiceMicButton";

const statusColorMap = {
  pending: { label: "Pending", bg: "#f1f5f9", text: "#475569" },
  in_progress: { label: "In Progress", bg: "#e0e7ff", text: "#3730a3" },
  inprogress: { label: "In Progress", bg: "#e0e7ff", text: "#3730a3" },
  blocked: { label: "Blocked", bg: "#fee2e2", text: "#991b1b" },
  review: { label: "Under Review", bg: "#fef3c7", text: "#92400e" },
  undereview: { label: "Under Review", bg: "#fef3c7", text: "#92400e" },
  completed: { label: "Completed", bg: "#dcfce7", text: "#166534" },
  approved: { label: "Approved", bg: "#dcfce7", text: "#166534" }
};

export default function WorkTaskDiscussionsModal({ open, onClose, work }) {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state?.loginState?.auth);
  const taskallState = useSelector((state) => state?.taskallState);
  const taskchatallState = useSelector((state) => state?.taskchatallState);

  const [selectedTask, setSelectedTask] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Filter tasks belonging strictly to the current work
  const tasks = (taskallState?.data?.results || []).filter(
    (t) => Number(t.work_id || t.work) === Number(work?.id)
  );

  const currentTaskId = selectedTask?.work_task_id || selectedTask?.id;

  // Fetch tasks for this work
  useEffect(() => {
    if (open && work?.id) {
      setSelectedTask(null);
      const query = `work/task/0/?filter_by={"work_id":${work.id}}`;
      dispatch({ type: `${TASKALL}_GET`, payload: query });
    } else {
      setSelectedTask(null);
    }
  }, [open, work?.id]);

  // Set default selected task when task list loads
  useEffect(() => {
    if (tasks.length > 0) {
      if (!selectedTask || !tasks.find((t) => String(t.id) === String(selectedTask.id))) {
        setSelectedTask(tasks[0]);
      }
    } else {
      setSelectedTask(null);
    }
  }, [taskallState?.data?.results, work?.id]);

  // Fetch chats for selected task
  const fetchChats = () => {
    if (currentTaskId) {
      const url = `task-chat/?task_id=${currentTaskId}`;
      dispatch({ type: `${TASKCHATALL}_GET`, payload: url });
    }
  };

  useEffect(() => {
    if (open && currentTaskId) {
      fetchChats();
      const interval = setInterval(fetchChats, 3500);
      return () => clearInterval(interval);
    }
  }, [open, currentTaskId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [taskchatallState?.data?.results]);

  const handleSend = () => {
    if (!message.trim() || !currentTaskId) return;
    setSending(true);

    dispatch({
      type: `${TASKCHAT}_POST`,
      payload: {
        task: currentTaskId,
        message: message.trim()
      },
      callback: () => {
        setMessage("");
        setSending(false);
        fetchChats();
      }
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const chats = currentTaskId
    ? (taskchatallState?.data?.results || []).filter(
        (c) => Number(c.task) === Number(currentTaskId)
      )
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          height: "680px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
        }
      }}
    >
      {/* Top Modal Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#ffffff"
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} fontSize={16} color="#0f172a">
            💬 Work Discussions: {work?.work_name || "Work"}
          </Typography>
          <Typography variant="caption" color="#64748b" sx={{ display: "block", mt: 0.2 }}>
            Client: <strong>{work?.client_name || "—"}</strong> &bull; Service: <strong>{work?.service_name || "—"}</strong> &bull; Manager: <strong>{work?.work_manager_name || "—"}</strong>
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#64748b", "&:hover": { bgcolor: "#f1f5f9" } }}>
          <MdClose size={22} />
        </IconButton>
      </Box>

      {/* Main Content Body: Flex 2 Columns */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
        {/* Left Side: Tasks Sidebar */}
        <Box
          sx={{
            width: "300px",
            minWidth: "260px",
            borderRight: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            display: "flex",
            flexDirection: "column",
            height: "100%"
          }}
        >
          <Box sx={{ px: 2, py: 1.2, borderBottom: "1px solid #f1f5f9", backgroundColor: "#f8fafc" }}>
            <Typography variant="caption" fontWeight={700} color="#475569" textTransform="uppercase" letterSpacing={0.6}>
              Tasks ({tasks.length})
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
            {taskallState?.loading && tasks.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : tasks.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center", color: "#94a3b8" }}>
                <Typography variant="body2" fontWeight={500}>No tasks found</Typography>
              </Box>
            ) : (
              tasks.map((t) => {
                const isSelected = selectedTask?.id === t.id;
                const st = statusColorMap[t.status] || { label: t.status, bg: "#f1f5f9", text: "#475569" };
                return (
                  <Box
                    key={t.id}
                    onClick={() => setSelectedTask(t)}
                    sx={{
                      p: 1.5,
                      mb: 1,
                      borderRadius: "10px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#eff6ff" : "#ffffff",
                      border: isSelected ? "1.5px solid #3b82f6" : "1px solid #e2e8f0",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        backgroundColor: isSelected ? "#eff6ff" : "#f8fafc",
                        borderColor: isSelected ? "#3b82f6" : "#cbd5e1"
                      }
                    }}
                  >
                    <Typography variant="body2" fontWeight={isSelected ? 700 : 600} color="#1e293b" sx={{ lineHeight: 1.3, mb: 0.8 }}>
                      {t.task_name}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="caption" color="#64748b" sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                        👤 {t.assigned_to_name || "Not Assigned"}
                      </Typography>
                      <Chip
                        label={st.label}
                        size="small"
                        sx={{
                          height: "20px",
                          fontSize: "10px",
                          fontWeight: 700,
                          backgroundColor: st.bg,
                          color: st.text,
                          borderRadius: "6px"
                        }}
                      />
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        {/* Right Side: Active Task Chat Conversation */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", minWidth: 0, backgroundColor: "#f8fafc" }}>
          {selectedTask ? (
            <>
              {/* Task Title Header Bar */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  backgroundColor: "#ffffff",
                  borderBottom: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <Box sx={{ minWidth: 0, pr: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="#0f172a" noWrap>
                    {selectedTask.task_name}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    Assigned: <strong>{selectedTask.assigned_to_name || "Not Assigned"}</strong>
                  </Typography>
                </Box>
                {selectedTask.status === "blocked" && selectedTask.remarks && (
                  <Chip
                    label={`🚫 Blocked: ${selectedTask.remarks}`}
                    size="small"
                    sx={{
                      backgroundColor: "#fee2e2",
                      color: "#991b1b",
                      fontWeight: 600,
                      fontSize: "11px",
                      maxWidth: "280px"
                    }}
                  />
                )}
              </Box>

              {/* Chat Messages Body */}
              <Box sx={{ flex: 1, overflowY: "auto", p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {taskchatallState?.loading && chats.length === 0 ? (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : chats.length === 0 ? (
                  <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", color: "#94a3b8", gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={600} color="#64748b">No messages yet on this task</Typography>
                    <Typography variant="caption">Send an instruction or note to the assigned employee.</Typography>
                  </Box>
                ) : (
                  chats.map((chat) => {
                    const isMe = chat.sender === currentUser?.id || chat.sender_name === currentUser?.username;
                    return (
                      <Box
                        key={chat.id}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: isMe ? "flex-end" : "flex-start",
                          maxWidth: "75%",
                          alignSelf: isMe ? "flex-end" : "flex-start"
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.4 }}>
                          <Typography variant="caption" fontWeight={600} color="#475569">
                            {isMe ? "You" : chat.sender_name}
                          </Typography>
                          {chat.sender_role && (
                            <Chip
                              label={chat.sender_role}
                              size="small"
                              sx={{
                                height: "18px",
                                fontSize: "10px",
                                fontWeight: 600,
                                backgroundColor: isMe ? "#e0e7ff" : "#f1f5f9",
                                color: isMe ? "#3730a3" : "#475569"
                              }}
                            />
                          )}
                          <Typography variant="caption" color="#94a3b8" fontSize="10px">
                            {chat.created_at_formatted}
                          </Typography>
                        </Box>

                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: isMe ? "14px 14px 2px 14px" : "14px 14px 14px 2px",
                            backgroundColor: isMe ? "#2563eb" : "#ffffff",
                            color: isMe ? "#ffffff" : "#1e293b",
                            border: isMe ? "none" : "1px solid #e2e8f0",
                            boxShadow: isMe ? "0 4px 6px -1px rgba(37, 99, 235, 0.2)" : "0 1px 3px rgba(0,0,0,0.05)",
                            wordBreak: "break-word"
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: "13px", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
                            {chat.message}
                          </Typography>
                        </Paper>
                      </Box>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Chat Input Bar Pinned at Bottom */}
              <Box
                sx={{
                  p: 1.5,
                  px: 2,
                  backgroundColor: "#ffffff",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Type an instruction or reply to assignee (Press Enter to send)..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  multiline
                  maxRows={3}
                  inputProps={{
                    style: {
                      color: "#0f172a",
                      fontSize: "13px",
                      lineHeight: "1.5"
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <VoiceMicButton
                        initialText={message}
                        onTranscript={(text) => setMessage(text)}
                      />
                    )
                  }}
                  sx={{
                    backgroundColor: "#ffffff",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#ffffff",
                      color: "#0f172a",
                      "& fieldset": {
                        borderColor: "#cbd5e1"
                      },
                      "&:hover fieldset": {
                        borderColor: "#94a3b8"
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#2563eb"
                      }
                    },
                    "& .MuiInputBase-input": {
                      color: "#0f172a !important",
                      WebkitTextFillColor: "#0f172a !important"
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#94a3b8 !important",
                      opacity: 1
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSend}
                  disabled={!message.trim() || sending}
                  sx={{
                    minWidth: "46px",
                    width: "46px",
                    height: "40px",
                    borderRadius: "10px",
                    backgroundColor: "#2563eb",
                    "&:hover": { backgroundColor: "#1d4ed8" },
                    p: 0
                  }}
                >
                  {sending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <MdSend size={18} />}
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", color: "#94a3b8", gap: 1 }}>
              <MdAssignment size={40} style={{ color: "#cbd5e1" }} />
              <Typography variant="body2" fontWeight={500}>Select a task on the left to start discussion</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
